import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * VerificationGate Component
 * Blocks unverified users from accessing protected features
 * 
 * Usage:
 * <VerificationGate 
 *   feature="create_listing"
 *   onVerified={() => handleAction()}
 * />
 */
function VerificationGate({ feature, onVerified, children }) {
  const navigate = useNavigate();
  const [user, setUser] = React.useState(null);
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleAction = async () => {
    // Not logged in - redirect to signup
    if (!user) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/signup');
      return;
    }

    // Not verified - show verification prompt
    if (user.accountTier !== 'verified') {
      setShowPrompt(true);

      // Track that user was prompted
      try {
        const token = localStorage.getItem('token');
        await fetch('/api/kyc/track-prompt', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            location: feature
          })
        });
      } catch (err) {
        console.error('Error tracking prompt:', err);
      }

      return;
    }

    // Verified - proceed
    if (onVerified) {
      onVerified();
    }
  };

  const handleVerifyNow = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/kyc/create-verification-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/verification-complete`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create verification session');
      }

      const data = await response.json();

      // Redirect to Stripe Identity verification
      window.location.href = data.url;

    } catch (err) {
      console.error('Error starting verification:', err);
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    setShowPrompt(false);

    // Track dismissal
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/kyc/prompt-dismissed', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          location: feature
        })
      });
    } catch (err) {
      console.error('Error tracking dismissal:', err);
    }
  };

  // Feature labels for display
  const featureLabels = {
    create_listing: 'list your property',
    make_offer: 'make an offer',
    contact_agents: 'contact agents',
    message_sellers: 'message sellers',
    schedule_showings: 'schedule showings'
  };

  const featureLabel = featureLabels[feature] || 'access this feature';

  return (
    <>
      {/* Render children with click handler */}
      <div onClick={handleAction}>
        {children}
      </div>

      {/* Verification Prompt Modal */}
      {showPrompt && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={handleDismiss}
        >
          <div 
            style={{
              background: '#fff',
              borderRadius: '16px',
              maxWidth: '480px',
              width: '100%',
              padding: '32px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#111827'
            }}>
              Verification Required
            </h2>

            <p style={{
              fontSize: '15px',
              color: '#6b7280',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              To {featureLabel}, we need to verify your identity. 
              This helps protect all parties in real estate transactions.
            </p>

            <div style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h4 style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                What you'll need:
              </h4>
              <ul style={{
                fontSize: '13px',
                color: '#6b7280',
                margin: 0,
                paddingLeft: '20px',
                lineHeight: '1.8'
              }}>
                <li>Government-issued photo ID</li>
                <li>2-3 minutes to complete</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleVerifyNow}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: loading ? '#9ca3af' : '#111827',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Starting...' : 'Verify Now'}
              </button>

              <button
                onClick={handleDismiss}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default VerificationGate;
